import { HR_RECORD_TYPES, recordModule, type DocumentClass, type Module } from './modules';
import { grantOpen, isInternal, isOpen, readsModulesWhole, type EnforcementMode, type Viewer } from './viewer';
import { t } from '../strings';
import type { HrRecordFacts } from './hrGuard';

/**
 * The guards of piece 8: canReadModule, recordIsInScope, canView,
 * assignedCompanies, canSeeField, canUseCapability and canTransition. Each
 * answers true in sandbox mode and evaluates the written rule otherwise.
 */
export interface GateContext {
  mode: EnforcementMode;
  onDate?: Date;
  /** The organisation's zone, where a gate reads a grant's last day as a calendar day (decision 0840); UTC when absent. */
  timeZone?: string;
}

const evaluates = (ctx: GateContext): boolean => ctx.mode !== 'sandbox';

/** The zone a grant's last day is read in: the gate's own, then the organisation's the viewer carries, then UTC (decision 0962). */
export const zoneOf = (viewer: Pick<Viewer, 'organizationTimeZone'>, ctx: Pick<GateContext, 'timeZone'>): string => ctx.timeZone ?? viewer.organizationTimeZone ?? 'UTC';

/** The rep's book of business: every RecordAssignment row with no endDate, as the Company set the read scope cascades from. */
export function assignedCompanies(viewer: Pick<Viewer, 'assignments' | 'employmentId'>, onDate: Date = new Date()): Set<string> {
  return new Set(viewer.assignments.filter((a) => a.employmentId === viewer.employmentId && isOpen(a, onDate)).map((a) => a.companyId));
}

/**
 * 🔒 The Deals whose team carries the viewer (decision 1632): every open
 * `DealTeamMember` row on the viewer's own employment, as the set the read
 * scope grants a document on, the way `assignedCompanies()` gives the accounts.
 */
export function teamDeals(viewer: Pick<Viewer, 'dealTeams' | 'employmentId'>, onDate: Date = new Date()): Set<string> {
  return new Set((viewer.dealTeams ?? []).filter((row) => row.employmentId === viewer.employmentId && isOpen(row, onDate)).map((row) => row.dealId));
}

/** The module-read half: the union of the role's read and any open Module-scoped grant naming the module (decision 0081). */
export function canReadModule(viewer: Viewer, module: Module, ctx: GateContext): boolean {
  if (!evaluates(ctx)) return true;
  const onDate = ctx.onDate ?? new Date();
  if (viewer.role?.modulePermissions[module]?.read) return true;
  return viewer.grants.some((g) => g.employmentId === viewer.employmentId && g.scopeKind === 'Module' && g.scopeId === module && g.capability === 'Read' && grantOpen(g, onDate, zoneOf(viewer, ctx)));
}

/** Write on a Module: the role's right, or an open Module-scoped Edit grant on the viewer's own employment (piece 17's two catalog actions read it). */
export function canWriteModule(viewer: Viewer, module: Module, ctx: GateContext): boolean {
  if (!evaluates(ctx)) return true;
  const onDate = ctx.onDate ?? new Date();
  if (viewer.role?.modulePermissions[module]?.write) return true;
  return viewer.grants.some((g) => g.employmentId === viewer.employmentId && g.scopeKind === 'Module' && g.scopeId === module && g.capability === 'Edit' && grantOpen(g, onDate, zoneOf(viewer, ctx)));
}

/**
 * 🔒 The eleven accounting documents a person writes (decision 0687). The
 * product owner, 2026-09-24: *"outside people can not create accounting
 * documents. Inside people not in accounting or assigned to account can't do
 * the documents either"*.
 */
export const WRITTEN_DOCUMENT_KINDS = ['Quote', 'SalesOrder', 'Invoice', 'CreditNote', 'PurchaseOrder', 'Bill', 'VendorCredit', 'RFQ', 'WorkOrder', 'ChangeOrder', 'Payment'] as const;
export type WrittenDocumentKind = (typeof WRITTEN_DOCUMENT_KINDS)[number];

/**
 * The Module each kind's own routes have always been gated on, so the module
 * half of the document write is the question the routes already asked. A
 * change order is its order's scope moving, and a request for a price is a
 * buyer's, so each answers with its parent's module (decision 0488).
 */
export const DOCUMENT_WRITE_MODULE: Readonly<Record<WrittenDocumentKind, Module>> = {
  Quote: 'Quote',
  SalesOrder: 'SalesOrder',
  Invoice: 'Invoice',
  CreditNote: 'Accounting',
  PurchaseOrder: 'PurchaseOrder',
  Bill: 'Accounting',
  VendorCredit: 'Accounting',
  RFQ: 'PurchaseOrder',
  WorkOrder: 'WorkOrder',
  ChangeOrder: 'SalesOrder',
  Payment: 'Accounting',
};

export function isWrittenDocumentKind(value: string): value is WrittenDocumentKind {
  return (WRITTEN_DOCUMENT_KINDS as readonly string[]).includes(value);
}

/**
 * What the document write guard reads about the document (decision 0687): its
 * kind, the Company it is addressed to, and the open team of the Deal it is on.
 * A create resolves the same facts from the Company and the Deal it names,
 * before the row exists.
 */
export interface DocumentWriteFacts {
  kind: WrittenDocumentKind;
  /** The Company the document is addressed to: the customer, or the vendor on the buying side. Null where it names none, such as a request for a price. */
  companyId: string | null;
  /** The employments on the document's Deal's team with no end date that has passed; empty where the document is on no Deal. */
  dealTeamEmploymentIds?: readonly string[];
  /** The Module the route gates on, where it is not the kind's own row above. */
  module?: Module;
}

/** Why the document write guard refused: an outside login, no write on the module, or neither accounting nor the account. */
export type DocumentWriteRefusal = 'External' | 'Module' | 'NotOnAccount';

/**
 * 🔒 canWriteDocument's reason (decision 0687). In order: the viewer is
 * internal; the role writes the kind's module, or an open Edit grant does; and
 * the viewer is in accounting — a role carrying the Accounting class — or is
 * assigned to the document's Company, or is on its Deal's open team. Null
 * where the write stands; sandbox mode answers null as every gate does.
 */
export function documentWriteRefusal(viewer: Viewer, facts: DocumentWriteFacts, ctx: GateContext): DocumentWriteRefusal | null {
  if (!evaluates(ctx)) return null;
  if (!isInternal(viewer)) return 'External';
  if (!canWriteModule(viewer, facts.module ?? DOCUMENT_WRITE_MODULE[facts.kind], ctx)) return 'Module';
  if (holdsDocumentClass(viewer, 'Accounting', ctx)) return null;
  const onDate = ctx.onDate ?? new Date();
  if (facts.companyId !== null && assignedCompanies(viewer, onDate).has(facts.companyId)) return null;
  if ((facts.dealTeamEmploymentIds ?? []).includes(viewer.employmentId)) return null;
  return 'NotOnAccount';
}

/** canWriteDocument: every create, edit, send and act on an accounting document asks this and nothing narrower (decision 0687). */
export function canWriteDocument(viewer: Viewer, facts: DocumentWriteFacts, ctx: GateContext): boolean {
  return documentWriteRefusal(viewer, facts, ctx) === null;
}

/** The refusal in words, the same on the server's answer and in the page's footer where the controls are withheld. */
export function documentWriteRefusalText(refusal: DocumentWriteRefusal): string {
  if (refusal === 'External') return t('document.write.refusedExternal');
  if (refusal === 'Module') return t('document.write.refusedModule');
  return t('document.write.refusedNotOnAccount');
}

/**
 * Whether + New is drawn at all, before any Company is chosen: the same guard
 * over the best case the viewer could name — any Company they are assigned to,
 * or any Deal whose open team carries them. 🔨 A create still asks the full
 * guard over what it names; this only decides whether the button is offered.
 */
export function mayStartDocument(viewer: Viewer, kind: WrittenDocumentKind, onAnyDealTeam: boolean, ctx: GateContext): boolean {
  if (!evaluates(ctx)) return true;
  const onDate = ctx.onDate ?? new Date();
  const [assigned] = [...assignedCompanies(viewer, onDate)];
  return canWriteDocument(viewer, { kind, companyId: assigned ?? null, dealTeamEmploymentIds: onAnyDealTeam ? [viewer.employmentId] : [] }, ctx);
}

/**
 * 🔒 What a document read asks `canView` over (decision 1632): the kind, the
 * row, the Company it is addressed to as the write guard resolves it — the
 * customer on the selling side, the vendor on the buying side — and the Deal it
 * is on. A request for a price names no one Company, so its Deal is its scope.
 */
export interface DocumentReadFacts {
  kind: WrittenDocumentKind;
  recordId: string;
  companyId: string | null;
  dealId: string | null;
  /** The Module the route gates on, where it is not the kind's own row in `DOCUMENT_WRITE_MODULE`. */
  module?: Module;
  /** 🔒 A Work Order's worker rows, the employments its lines name; no other kind answers it (decision 1632). */
  workerEmploymentIds?: readonly string[];
}

/**
 * 🔒 canView over a document's own facts (decision 1632). The module read the
 * route has always asked comes first. An unrestricted internal role then reads
 * the document whole (decision 3460), through the scope's `Internal` reason.
 * On a restricted role, in order: a role carrying the
 * Accounting class reads every one of the eleven kinds, as it writes them; a
 * role whose view-all covers the module the read is gated on reads every one
 * of that read; and otherwise the record scope, an open assignment on the
 * document's Company, an open row on its Deal's team, or a worker's own line
 * on a Work Order, with the Purchase Order carved out of the first two.
 * 🔨 The view-all follows the gated module, so a change order is read on the
 * Sales Order module its routes ask, and an invoice raised from a sales order
 * reads that order on the Invoice module.
 */
export function documentReadAllowed(viewer: Viewer, facts: DocumentReadFacts, ctx: GateContext): boolean {
  if (!evaluates(ctx)) return true;
  if (!viewer.employmentActive) return false;
  const module = facts.module ?? DOCUMENT_WRITE_MODULE[facts.kind];
  if (!canReadModule(viewer, module, ctx)) return false;
  if (holdsDocumentClass(viewer, 'Accounting', ctx)) return true;
  if (isInternal(viewer) && (viewer.role?.viewAllInModules.includes(module) ?? false)) return true;
  // 🔨 The record's own module's view-all is not asked here: the line above asked the gated module's, which is the one this read is.
  const grant = recordScopeGrant(viewer, { recordType: facts.kind, recordId: facts.recordId, companyId: facts.companyId, dealId: facts.dealId, ...(facts.workerEmploymentIds ? { workerEmploymentIds: facts.workerEmploymentIds } : {}) }, ctx);
  return grant !== null && grant !== 'Override';
}

/** The refusal a document page draws in place of the generic Access Denied sentence (decision 1632). */
export function documentReadRefusalText(): string {
  return t('document.read.restricted');
}

/**
 * What a scope check reads about the record: its type, its own companyId where
 * it has one, and the joins the external and the assignment rules resolve
 * through. No PersonRecord carries a company id; the relationship is a row.
 */
export interface RecordFacts {
  recordType: string;
  recordId: string;
  /** The record's own companyId: a Site, an Employment and every document; a Company answers with its id. */
  companyId?: string | null;
  /** For a PersonRecord: the Companies the person holds an active EmploymentRecord at. */
  activeEmploymentCompanyIds?: readonly string[];
  /** For a LocationRecord: the Companies an active SiteRecord joins it to. */
  activeSiteCompanyIds?: readonly string[];
  /** For an AssetNode: the Companies AssetCompanyLink joins it to. */
  assetCompanyIds?: readonly string[];
  /** Who created the record; authorship keeps access to a record and its documents regardless of class (decision 0074). */
  createdByPersonId?: string | null;
  /** 🔒 The Deal a document is on, which its team reads it through (decision 1632); a Deal answers with its own id. */
  dealId?: string | null;
  /** 🔒 The employments a Work Order's worker rows name, which opens the job to each of them (decision 1632); read on a Work Order alone. */
  workerEmploymentIds?: readonly string[];
  /** 🔒 An HR record's own facts, resolved as at its date: what `canViewHrRecord` reads, and nothing else does (piece 34). */
  hr?: HrRecordFacts;
}

/** The Companies a record resolves to, by the join its type uses and never by a column put there for the cascade. */
export function recordCompanies(record: RecordFacts): readonly string[] {
  switch (record.recordType) {
    case 'CompanyRecord':
      return [record.recordId];
    case 'PersonRecord':
      return record.activeEmploymentCompanyIds ?? [];
    case 'LocationRecord':
      return record.activeSiteCompanyIds ?? [];
    case 'AssetNode':
      return record.assetCompanyIds ?? [];
    default:
      return record.companyId ? [record.companyId] : [];
  }
}

/** The Deal a record is on, for the team grant: a Deal is its own, a document names its own, and every other record is on none (decision 1632). */
export function recordDeal(record: RecordFacts): string | null {
  if (record.recordType === 'Deal') return record.recordId;
  return record.dealId ?? null;
}

/** A non-employee contact is scoped strictly to the Company on the EmploymentRecord they signed in through, by join, not by a column comparison. */
export function externalRecordInScope(viewer: Pick<Viewer, 'companyId'>, record: RecordFacts): boolean {
  return recordCompanies(record).includes(viewer.companyId);
}

/**
 * The record-level half. An internal employment on an unrestricted role reads
 * every record (decision 3460). A restricted role reads viewAllInModules, the
 * assignment cascade through assignedCompanies(), then the Deal's team through
 * teamDeals() (decision 1632), with PurchaseOrder carved out of both even for an
 * assigned Company. An external session takes the join rule instead.
 */
export function recordIsInScope(viewer: Viewer, record: RecordFacts, ctx: GateContext): boolean {
  return recordScopeGrant(viewer, record, ctx) !== null;
}

/**
 * Why a record is in scope (piece 21, Per-module view and edit override):
 * `Sandbox` where no gate evaluates, `External` by the join rule, `Internal`
 * by an unrestricted role's default reach (decision 3460), `Override` by
 * `viewAllInModules` alone, `Assignment` through the book of business,
 * `DealTeam` through an open team row on the record's Deal, `OwnWork` through a
 * worker row naming the viewer on a Work Order's line (decision 1632). The
 * override is asked last, so a record an assignment already brings into scope
 * is never reported as the override's doing, and the audit line the override
 * owes is written for the reads only it permitted. 🔨 `Internal` is answered
 * before an assignment is read, so an assignment widens nothing an unrestricted
 * role already has, and the view-all is logged on a restricted role alone.
 * Null is out of scope.
 */
export type ScopeGrant = 'Sandbox' | 'External' | 'Internal' | 'Override' | 'Assignment' | 'DealTeam' | 'OwnWork';

export function recordScopeGrant(viewer: Viewer, record: RecordFacts, ctx: GateContext): ScopeGrant | null {
  if (!evaluates(ctx)) return 'Sandbox';
  const onDate = ctx.onDate ?? new Date();
  if (!isInternal(viewer)) return externalRecordInScope(viewer, record) ? 'External' : null;
  // 🔒 The default reach: every record of a module the role reads, the Purchase Order among them (decision 3460).
  // 🔨 Never an HR record: that answer stays canViewHrRecord's, and a file on one keeps today's scope step.
  if (readsModulesWhole(viewer) && !(HR_RECORD_TYPES as readonly string[]).includes(record.recordType)) return 'Internal';
  const module = recordModule(record.recordType);
  // 🔨 PurchaseOrder stays carved out of the cascade, and of the team grant beside it: only the override reads an order (decision 1632).
  const carvedOut = record.recordType === 'PurchaseOrder';
  const byAssignment = !carvedOut && recordCompanies(record).some((companyId) => assignedCompanies(viewer, onDate).has(companyId));
  if (byAssignment) return 'Assignment';
  const dealId = recordDeal(record);
  if (!carvedOut && dealId !== null && teamDeals(viewer, onDate).has(dealId)) return 'DealTeam';
  // 🔒 A worker opens the job their own line names, and nothing else by it: the grant is read on a Work Order alone (decision 1632).
  if (record.recordType === 'WorkOrder' && viewer.employmentId !== '' && (record.workerEmploymentIds ?? []).includes(viewer.employmentId)) return 'OwnWork';
  if (viewer.role?.viewAllInModules.includes(module)) return 'Override';
  return null;
}

/**
 * An HR record resolves through canViewHrRecord alone; HR owns that guard (`./hrGuard`), and a caller that passes none is refused.
 * 🔒 The module step and the scope step stay for the list views (piece 34; T853), so an employee whose role reads no Person still opens their own record.
 */
export type HrRecordGuard = (viewer: Viewer, record: RecordFacts) => boolean;

/** canView: the module read gate first, then the record scope check. An unrestricted internal role reads the module whole; a restricted one with no RecordAssignment sees nothing (decision 3460). */
export function canView(viewer: Viewer, record: RecordFacts, ctx: GateContext, canViewHrRecord?: HrRecordGuard): boolean {
  return canViewWithGrant(viewer, record, ctx, canViewHrRecord).allowed;
}

/** canView with its reason: the same boolean every caller already reads, and the scope grant that produced it, `Override` alone being the one that is logged. */
export function canViewWithGrant(viewer: Viewer, record: RecordFacts, ctx: GateContext, canViewHrRecord?: HrRecordGuard): { allowed: boolean; grant: ScopeGrant | null } {
  if (!evaluates(ctx)) return { allowed: true, grant: 'Sandbox' };
  if (!viewer.employmentActive) return { allowed: false, grant: null };
  if ((HR_RECORD_TYPES as readonly string[]).includes(record.recordType)) {
    // 🔒 The guard is the record-level answer for an HR record (piece 34; T853): no module step and no scope grant follow it.
    return { allowed: canViewHrRecord !== undefined && canViewHrRecord(viewer, record), grant: null };
  }
  if (!canReadModule(viewer, recordModule(record.recordType), ctx)) return { allowed: false, grant: null };
  const grant = recordScopeGrant(viewer, record, ctx);
  return { allowed: grant !== null, grant };
}

/**
 * The per-module view override on its own, for a record whose scope is a person
 * rather than a Company (piece 21, Per-module view and edit override). An
 * expense report belongs to whoever raised it, so `RecordFacts` has no company
 * to compare and this is the whole of the question: does this role read every
 * record of the module, or only its own? 🔨 The default reach of decision 3460
 * is a Company's records, so it is not read here: a person's own records stay
 * the view-all's on any role.
 */
export function viewsAllInModule(viewer: Viewer, module: Module, ctx: GateContext): boolean {
  if (!evaluates(ctx)) return true;
  if (!isInternal(viewer)) return false;
  return viewer.role?.viewAllInModules.includes(module) ?? false;
}

/** The Deals a list may show a viewer whose role does not read every Deal: their own creations and the Deals they are on the team of. */
export interface DealListScope {
  createdByPersonId: string;
  teamDealIds: readonly string[];
}

/**
 * 🔒 Which Deals the Deals list and board show this viewer (T924, decision
 * 1712). A role the Deal page opens every Deal for, the module's view-all,
 * sees every Deal, as does a sandbox read; anybody else sees the Deals they
 * created and the Deals they stand on the team of, and nothing else. The
 * product owner, 2026-10-03: *"the deal list should only show the salesperson
 * deals they created or are assigned to, only."* Null is every Deal.
 * 🔒 An unrestricted internal role reads every Deal, so it lists every Deal;
 * the narrowing binds a restricted role alone (decision 3460).
 */
export function dealListScope(viewer: Viewer, ctx: GateContext): DealListScope | null {
  if (!evaluates(ctx)) return null;
  if (readsModulesWhole(viewer)) return null;
  if (viewsAllInModule(viewer, 'Deal', ctx)) return null;
  return { createdByPersonId: viewer.personId, teamDealIds: [...teamDeals(viewer, ctx.onDate ?? new Date())] };
}

/** One capability key rather than a Module: the string a work queue and its count are gated by, from the role's capabilities set. */
export function canUseCapability(viewer: Viewer, key: string, ctx: GateContext): boolean {
  if (!evaluates(ctx)) return true;
  return viewer.role?.capabilities.includes(key) ?? false;
}

/** A role holds a class as a capability, evaluated only for an active internal employment (decision 0068). */
export function holdsDocumentClass(viewer: Viewer, documentClass: DocumentClass, ctx: GateContext): boolean {
  if (!evaluates(ctx)) return true;
  if (!isInternal(viewer)) return false;
  return viewer.role?.documentClasses.includes(documentClass) ?? false;
}

/** One row per field withheld from an audience (decision 0125). */
export interface FieldVisibilityRuleRow {
  entity: string;
  field: string;
  audienceKind: 'SystemRole' | 'External';
  /** A SystemRole row naming no role speaks for every role; a row naming one wins over it. */
  systemRoleId: string | null;
  isVisible: boolean;
  isActive: boolean;
}

/**
 * canSeeField(viewer, entity, field): the serialiser omits a refused field
 * from the payload rather than blanking it. An external viewer meets the
 * External rows; an internal viewer meets the role rows, the row naming their
 * role first and the row naming none second; a field with no row is visible.
 */
export function canSeeField(viewer: Viewer, entity: string, field: string, rules: readonly FieldVisibilityRuleRow[], ctx: GateContext): boolean {
  if (!evaluates(ctx)) return true;
  const applicable = rules.filter((r) => r.isActive && r.entity === entity && r.field === field);
  if (!isInternal(viewer)) {
    const external = applicable.filter((r) => r.audienceKind === 'External');
    return external.length === 0 ? true : external.every((r) => r.isVisible);
  }
  const roleId = viewer.role?.id ?? null;
  const named = applicable.find((r) => r.audienceKind === 'SystemRole' && r.systemRoleId !== null && r.systemRoleId === roleId);
  if (named) return named.isVisible;
  const general = applicable.find((r) => r.audienceKind === 'SystemRole' && r.systemRoleId === null);
  return general ? general.isVisible : true;
}

/** The transitions an enum constrains itself with; an enum with no rows accepts any move among its members. */
export interface StatusTransitionRow {
  entityType: string;
  fromStatus: string;
  toStatus: string;
  isActive: boolean;
}

/**
 * canTransition(viewer, record, from, to): the one state-machine guard on the
 * write path (decision 0093), reading StatusTransition rows keyed on the
 * enum's name. Rows constrain; no rows means any transition among members.
 */
export function canTransition(viewer: Viewer, entityType: string, from: string, to: string, rows: readonly StatusTransitionRow[], ctx: GateContext): boolean {
  if (!evaluates(ctx)) return true;
  if (!viewer.employmentActive) return false;
  const forEntity = rows.filter((r) => r.entityType === entityType && r.isActive);
  if (forEntity.length === 0) return true;
  return forEntity.some((r) => r.fromStatus === from && r.toStatus === to);
}

/**
 * The two login-creation guards (piece 21): holding the first grants nothing
 * of the second, since an external login exposes a customer's data to someone
 * outside the building. Both read a capability key on the viewer's role, and
 * a super admin holds both through the seeded Admin role's own keys.
 */
export function canCreateInternalLogin(viewer: Viewer, ctx: GateContext): boolean {
  return canUseCapability(viewer, 'logins.createInternal', ctx);
}

export function canCreateExternalLogin(viewer: Viewer, employment: { companyId: string }, ctx: GateContext): boolean {
  if (employment.companyId === viewer.organizationCompanyId) return canCreateInternalLogin(viewer, ctx);
  return canUseCapability(viewer, 'logins.createExternal', ctx);
}
